import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Box, Typography, Button, TextField, MenuItem, Autocomplete } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import SearchIcon from '../../../_icons/SearchIcon';
import { useSelector } from 'react-redux';
import QueryString from 'qs';
import { getMemberByBranchAndTemporary } from '../../../api/member-management';
import { getGolfDefaultSettingsByAreaId, getSpotWaitingInfoAll, reserveGolfSpot } from '../../../api/golf';
import { getUserTickets } from '../../../api/user';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';
import GolfSpotHistory from '../GolfSpotHistory';
import GolfSpotUserInfo from '../GolfSpotUserInfo';
import { useQuery, useMutation } from '@tanstack/react-query';
import GolfReservationDeleteModal from './GolfReservationDeleteModal';
import GolfReservationManageModal from './GolfReservationManageModal';

const GolfSpotDetailModal = ({
  open,
  handleClose,
  spotData,
  onExtendTimeClick,
  onWaitingListClick,
  onMoveSpot,
  onReservationSuccess,
  onLoadingStart,
  onLoadingEnd,
}) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const { control, setValue, getValues } = useForm({
    defaultValues: {
      memberMemo: '',
      spotMemo: '',
      memberSearch: '',
      ticket: '',
      startTime: '',
      endTime: '',
      remainingTime: '',
      expirationDate: '',
      memberCoursePassId: '',
    },
  });

  const bufferTimeRef = useRef(0);
  const isSellClickRef = useRef(false); // Sell 클릭으로 인한 시간 설정 플래그
  const [memberSearchInput, setMemberSearchInput] = useState(''); // 검색어 입력값 (debounce용)
  const [debouncedSearchInput, setDebouncedSearchInput] = useState(''); // debounce된 검색어
  const [selectedMemberId, setSelectedMemberId] = useState(null); // 선택된 회원 ID
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [isReservationTimeManuallyEdited, setIsReservationTimeManuallyEdited] = useState(false);
  const [isStartTimeFocused, setIsStartTimeFocused] = useState(false);
  const [isEndTimeFocused, setIsEndTimeFocused] = useState(false);
  // 시작시간의 초 정보를 저장 (종료시간 + 5초 계산 시 사용)
  const startTimeSecondsRef = useRef(null);
  const [usageMinute, setUsageMinute] = useState(0); // 이용시간 (기본값 30분)
  const [bufferMinute, setBufferMinute] = useState(0); // 여유시간 (기본값 0분)
  const [fieldErrors, setFieldErrors] = useState({
    memberSearch: false,
    ticket: false,
  });
  const [confirmEndModalOpen, setConfirmEndModalOpen] = useState(false); // 종료 확인 모달 상태
  const [reservationManageModalOpen, setReservationManageModalOpen] = useState(false); // 예약 변경 모달 상태
  const [selectedReservationData, setSelectedReservationData] = useState(null); // 선택된 예약 데이터
  // ISO 8601 형식의 시간을 시분 형식으로 변환
  const formatTimeFromISO = useCallback(isoString => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      // 이미 시분 형식이거나 파싱 실패 시 그대로 반환
      return isoString;
    }
  }, []);

  // 시작 시간 자동 계산 함수
  const calculateStartTime = useCallback(() => {
    const now = new Date();
    const currentHour = String(now.getHours()).padStart(2, '0');
    const currentMinute = String(now.getMinutes()).padStart(2, '0');
    return `${currentHour}:${currentMinute}`;
  }, []);

  // 시간 문자열에 분을 더하는 함수
  const addMinutesToTimeString = useCallback((timeString, minutesToAdd) => {
    if (!timeString) return timeString;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    const newDate = new Date(date.getTime() + minutesToAdd * 60000);
    const newHours = String(newDate.getHours()).padStart(2, '0');
    const newMinutes = String(newDate.getMinutes()).padStart(2, '0');
    return `${newHours}:${newMinutes}`;
  }, []);

  // 시간 문자열에 초를 더하는 함수
  const addSecondsToTimeString = useCallback((timeString, secondsToAdd) => {
    if (!timeString) return timeString;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    const newDate = new Date(date.getTime() + secondsToAdd * 1000);
    const newHours = String(newDate.getHours()).padStart(2, '0');
    const newMinutes = String(newDate.getMinutes()).padStart(2, '0');
    return `${newHours}:${newMinutes}`;
  }, []);

  // 종료 시간 계산 함수 (시작 시간 기준)
  // 종료시간 = 시작시간 + 이용시간 + 여유시간
  const calculateEndTime = useCallback(
    (startTimeString, usageTime = usageMinute, bufferTime = bufferMinute) => {
      if (!startTimeString) return '';

      const [hours, minutes] = startTimeString.split(':').map(Number);
      const startDate = new Date();
      startDate.setHours(hours, minutes, 0, 0);

      // 종료시간 = 시작시간 + 이용시간 + 여유시간
      const totalMinutes = (usageTime || 30) + (bufferTime || 0);
      const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
      const endHour = String(endDate.getHours()).padStart(2, '0');
      const endMinute = String(endDate.getMinutes()).padStart(2, '0');

      return `${endHour}:${endMinute}`;
    },
    [usageMinute, bufferMinute],
  );

  // 시간 입력 유효성 검사 및 포맷팅 함수
  const formatTimeInput = value => {
    let replaceTime = value.replace(/:/g, '');
    replaceTime = replaceTime.replace(/\D/g, '');
    if (replaceTime.length > 4) {
      replaceTime = replaceTime.substring(0, 4);
    }
    if (replaceTime.length >= 4) {
      const hours = replaceTime.substring(0, 2);
      const minute = replaceTime.substring(2, 4);
      if (!isFinite(hours + minute)) {
        return '00:00';
      }
      if (parseInt(hours + minute) >= 2400) {
        return '23:59';
      }
      if (parseInt(minute) >= 60) {
        return hours + ':00';
      }
      return hours + ':' + minute;
    }
    return replaceTime;
  };

  // debounce된 검색어 업데이트
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchInput(memberSearchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [memberSearchInput]);

  // 회원 검색 (React Query)
  const { data: membersData, isLoading: isSearchingMembers } = useQuery({
    queryKey: [
      'searchMembers',
      user?.token,
      user?.headBranch?.headBranchId,
      user?.branch?.branchId,
      debouncedSearchInput,
    ],
    queryFn: async () => {
      if (!debouncedSearchInput || debouncedSearchInput.trim().length === 0) {
        return [];
      }
      const qstring = QueryString.stringify({
        searchInput: debouncedSearchInput,
        headBranchId: user.headBranch?.headBranchId,
        branchId: user.branch?.branchId,
      });
      const data = await getMemberByBranchAndTemporary(user.token, qstring);
      return (
        data?.content?.map(item => ({
          id: item.memberId,
          name: item.name + ' (' + item.phoneNumber + ')',
          userType: item.userType,
        })) || []
      );
    },
    enabled:
      !!user?.token &&
      !!user?.headBranch?.headBranchId &&
      !!user?.branch?.branchId &&
      debouncedSearchInput.trim().length > 0,
    staleTime: 0,
  });

  const searchResults = membersData || [];

  // 회원의 이용권 목록 가져오기 (React Query)
  const { data: ticketsData, isLoading: isLoadingTickets } = useQuery({
    queryKey: ['memberTickets', user?.token, user?.headBranch?.headBranchId, user?.branch?.branchId, selectedMemberId],
    queryFn: async () => {
      if (!selectedMemberId || !user?.token) {
        return [];
      }
      const query = `headBranchId=${user?.headBranch?.headBranchId || ''}&branchId=${user?.branch?.branchId || ''}`;
      const data = await getUserTickets(selectedMemberId, user.token, query);

      let allTickets = [];
      if (data) {
        // activePasses에서 sportType이 "GOLF"인 것만 필터링
        if (data.activePasses && Array.isArray(data.activePasses)) {
          const golfPasses = data.activePasses.filter(pass => pass.sportType === 'GOLF');
          allTickets = [...allTickets, ...golfPasses];
        }
      }

      if (allTickets.length > 0) {
        return allTickets.map(item => ({
          value: item.memberCourseTicketId || item.memberCoursePassId || item.id,
          label:
            item.ticketName || item.name || `이용권 ${item.memberCourseTicketId || item.memberCoursePassId || item.id}`,
          ...item,
        }));
      }
      return [];
    },
    enabled: !!user?.token && !!selectedMemberId && !!user?.headBranch?.headBranchId && !!user?.branch?.branchId,
    staleTime: 0,
  });

  const ticketOptions = useMemo(() => ticketsData || [], [ticketsData]);

  // 스팟 기본 설정 조회
  const { data: basicSettingsData, refetch } = useQuery({
    queryKey: ['golfSpotBasicSetting', user?.token, spotData?.areaId],
    queryFn: () => getGolfDefaultSettingsByAreaId(user?.token, spotData?.areaId),
    enabled: !!user?.token && !!spotData?.areaId && open,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    select: data => ({
      bufferMinute: data?.bufferMinute,
      usageMinute: data?.usageMinute,
    }),
  });

  useEffect(() => {
    if (open && user?.token && spotData?.golfAreaId) {
      refetch();
    }
  }, [open, user?.token, spotData?.golfAreaId, refetch]);

  // 기본 설정 데이터가 로드되면 state 업데이트
  useEffect(() => {
    if (basicSettingsData) {
      if (basicSettingsData.usageMinute !== undefined) {
        setUsageMinute(basicSettingsData.usageMinute);
      }
      if (basicSettingsData.bufferMinute !== undefined) {
        setBufferMinute(basicSettingsData.bufferMinute);
        bufferTimeRef.current = basicSettingsData.bufferMinute;
      }
    }
  }, [basicSettingsData]);

  // 대기자 목록 조회
  const { data: waitingData } = useQuery({
    queryKey: ['spotWaitingList', user?.token, spotData?.areaId, spotData?.id],
    queryFn: () => getSpotWaitingInfoAll(user.token, spotData?.areaId),
    enabled: !!open && !!user?.token && !!spotData?.areaId && !!spotData?.id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: false,
    select: data => {
      if (!data || !Array.isArray(data.spots)) {
        return { waitingList: [] };
      }
      const matchedSpot = data.spots.find(spot => spot.spotId === spotData.id);
      if (!matchedSpot || !Array.isArray(matchedSpot.waitingList)) {
        return { waitingList: [] };
      }
      return { waitingList: matchedSpot.waitingList || [] };
    },
  });

  const waitingList = useMemo(() => waitingData?.waitingList || [], [waitingData?.waitingList]);

  // 예약 데이터가 있는지 확인
  const hasReservation = spotData?.reservation?.status === 'RESERVED' || spotData?.reservation?.status === 'RUNNING';
  const isRunning = spotData?.reservation?.status === 'RUNNING';

  // 예약 생성 (React Query Mutation)
  const reservationMutation = useMutation({
    mutationFn: async requestData => {
      return await reserveGolfSpot(user.token, spotData.id, requestData);
    },
    onSuccess: response => {
      if (response === undefined || response === null) {
        toast.error('예약 처리 중 오류가 발생했습니다.');
        return;
      }
      toast.success(hasReservation ? '대기 예약이 완료되었습니다.' : '배정이 완료되었습니다.');

      // 예약 성공 콜백 호출 (로딩 상태 설정을 위해)
      if (onReservationSuccess) {
        // 고유한 spot.id만 전달 (spotNumber나 spotName은 겹칠 수 있음) => 스켈레톤을 위함
        const spotId = spotData?.id;
        onReservationSuccess(spotId);
      } else {
        handleClose();
      }
    },
    onError: error => {
      console.error('handleAssignmentComplete 에러:', error);
      toast.error(errorMessages(error));
    },
  });

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1512px',
    maxWidth: '95vw',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const spotNumber = spotData?.spotNumber || spotData?.id?.replace('S', '') || '6';
  const memberMemo = spotData?.memberMemo || '';
  const spotMemo = spotData?.spotMemo || '';
  const reservationTime = spotData?.reservationTime || '19:50 ~ 20:43';

  // 모달이 열릴 때 초기화 (open 변경 시에만 실행)
  useEffect(() => {
    if (open) {
      // Sell 클릭 플래그 초기화
      isSellClickRef.current = false;
      // 게스트 모드 초기화
      setIsGuestMode(false);
      setGuestMode(false);
      // 검색어 초기화
      setMemberSearchInput('');
      setDebouncedSearchInput('');
      setSelectedMemberId(null);
      // 폼 필드 초기화
      setValue('memberSearch', '');
      setValue('ticket', '');
      setValue('expirationDate', '');
      setValue('memberMemo', '');
    }
  }, [open, setValue]);

  // 모달이 열릴 때 시간 관련 초기화
  useEffect(() => {
    if (open && !isSellClickRef.current) {
      // bufferTimeRef를 현재 bufferTime 값으로 동기화
      bufferTimeRef.current = bufferMinute;

      // 예약이 있는 경우 (RESERVED 또는 RUNNING): 예약의 종료시간을 기준으로 시작시간 설정
      if (hasReservation && spotData?.reservation?.endTime) {
        // ISO 문자열에서 직접 초까지 포함해서 가져오기
        let endDate;
        try {
          endDate = new Date(spotData.reservation.endTime);
        } catch (error) {
          endDate = null;
        }

        if (endDate && !isNaN(endDate.getTime())) {
          let startTime;
          let totalWaitingMinutes = 0;

          // 모든 대기자들의 시간 합산 (여유시간 + 이용시간)
          if (waitingList.length > 0) {
            totalWaitingMinutes = waitingList.reduce((total, waiting) => {
              const waitingBufferMinute = waiting.bufferMinute || bufferTimeRef.current;
              const waitingUsageMinute = waiting.usageMinute || usageMinute;
              return total + waitingBufferMinute + waitingUsageMinute;
            }, 0);
          }

          // 현재 이용자의 종료시간(초 포함) + 모든 대기자들의 시간 합산 + 5초 = 시작시간
          const startDate = new Date(endDate.getTime() + totalWaitingMinutes * 60000 + 5000); // 5초 추가
          const startHours = String(startDate.getHours()).padStart(2, '0');
          const startMinutes = String(startDate.getMinutes()).padStart(2, '0');
          const startSeconds = String(startDate.getSeconds()).padStart(2, '0');
          startTime = `${startHours}:${startMinutes}`;

          // 초 정보를 ref에 저장 (전송 시 사용)
          startTimeSecondsRef.current = startSeconds;

          setValue('startTime', startTime);
          // 종료시간 = 시작시간 + 이용시간 + 여유시간
          const calculatedEndTime = calculateEndTime(startTime, usageMinute, bufferMinute);
          setValue('endTime', calculatedEndTime);
          setIsReservationTimeManuallyEdited(false);
          setIsStartTimeFocused(false);
          setIsEndTimeFocused(false);
        }
      } else if (!hasReservation && !isRunning) {
        // 예약이 없고 RUNNING도 아닐 때: 현재 시간 = 시작시간 (여유시간 제외)
        const currentTime = calculateStartTime();
        const calculatedEndTime = calculateEndTime(currentTime);
        setValue('startTime', currentTime);
        setValue('endTime', calculatedEndTime);
        setIsReservationTimeManuallyEdited(false);
        setIsStartTimeFocused(false);
        setIsEndTimeFocused(false);
      }
      // spotMemo 초기값 설정
      setValue('spotMemo', spotMemo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    hasReservation,
    isRunning,
    spotData?.reservation?.endTime,
    spotMemo,
    bufferMinute,
    usageMinute,
    waitingList,
    setValue,
    calculateStartTime,
    calculateEndTime,
    formatTimeFromISO,
    addMinutesToTimeString,
    addSecondsToTimeString,
  ]);

  // 이용시간 또는 여유시간이 변경될 때 종료 시간 자동 업데이트
  useEffect(() => {
    // RUNNING 상태가 아니고 예약이 없을 때만 실행, 그리고 Sell 클릭으로 인한 설정이 아닐 때만
    if (!hasReservation && !isRunning && !isStartTimeFocused && !isEndTimeFocused && !isSellClickRef.current) {
      const startTime = getValues('startTime');
      if (startTime) {
        const calculatedEndTime = calculateEndTime(startTime);
        setValue('endTime', calculatedEndTime);
      } else {
        const calculatedStartTime = calculateStartTime();
        const calculatedEndTime = calculateEndTime(calculatedStartTime);
        setValue('startTime', calculatedStartTime);
        setValue('endTime', calculatedEndTime);
      }
    }
  }, [
    usageMinute,
    bufferMinute,
    hasReservation,
    isRunning,
    isStartTimeFocused,
    isEndTimeFocused,
    setValue,
    getValues,
    calculateStartTime,
    calculateEndTime,
  ]);

  // 실시간으로 시작 시간 업데이트 (사용자가 수정하지 않고 포커스도 없는 경우에만)
  useEffect(() => {
    // RUNNING 상태가 아니고 예약이 없을 때만 실행, 그리고 Sell 클릭으로 인한 설정이 아닐 때만
    if (
      !hasReservation &&
      !isRunning &&
      !isReservationTimeManuallyEdited &&
      !isStartTimeFocused &&
      !isEndTimeFocused &&
      !isSellClickRef.current
    ) {
      const baseTime = calculateStartTime();
      const calculatedEndTime = calculateEndTime(baseTime);
      setValue('startTime', baseTime);
      setValue('endTime', calculatedEndTime);

      const interval = setInterval(() => {
        // RUNNING 상태가 아닐 때만 업데이트, 그리고 Sell 클릭으로 인한 설정이 아닐 때만
        if (
          !isRunning &&
          !isReservationTimeManuallyEdited &&
          !isStartTimeFocused &&
          !isEndTimeFocused &&
          !isSellClickRef.current
        ) {
          const baseTime = calculateStartTime();
          const currentStartTime = getValues('startTime');
          if (currentStartTime !== baseTime) {
            const updatedEndTime = calculateEndTime(baseTime);
            setValue('startTime', baseTime);
            setValue('endTime', updatedEndTime);
          }
        }
      }, 1000); // 1초마다 체크하되, 값이 변경될 때만 업데이트

      return () => clearInterval(interval);
    }
  }, [
    hasReservation,
    isRunning,
    isReservationTimeManuallyEdited,
    isStartTimeFocused,
    isEndTimeFocused,
    setValue,
    getValues,
    calculateStartTime,
    calculateEndTime,
    addMinutesToTimeString,
  ]);

  // 시간 문자열을 날짜+시간 형식으로 변환
  const formatDateTime = (timeString, isStartTime = false) => {
    if (!timeString) return null;
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
      today.getDate(),
    ).padStart(2, '0')}`;

    // 시작시간이고 초 정보가 저장되어 있으면 그 초를 사용, 아니면 현재 시간의 초 사용
    let seconds;
    if (isStartTime && startTimeSecondsRef.current !== null) {
      seconds = startTimeSecondsRef.current;
    } else {
      seconds = String(today.getSeconds()).padStart(2, '0');
    }

    const timeWithSeconds = timeString.split(':').length === 3 ? timeString : `${timeString}:${seconds}`;
    return `${todayString} ${timeWithSeconds}`;
  };

  // 회원 모드 유효성 검사
  const validateMemberMode = (formValues, actionType) => {
    if (!formValues.memberSearch) {
      setFieldErrors(prev => ({ ...prev, memberSearch: true }));
      toast.error('회원을 선택해주세요.');
      return { isValid: false };
    }

    if (!formValues.ticket) {
      setFieldErrors(prev => ({ ...prev, ticket: true }));
      toast.error(`${actionType}을 위해 이용권을 선택해주세요.`);
      return { isValid: false };
    }

    const selectedTicket = ticketOptions.find(
      option => option.value === formValues.ticket || option.memberCoursePassId === formValues.ticket,
    );

    if (!selectedTicket?.coursePassId) {
      setFieldErrors(prev => ({ ...prev, ticket: true }));
      toast.error(`${actionType}을 위해 이용권을 선택해주세요.`);
      return { isValid: false };
    }

    return { isValid: true, coursePassId: selectedTicket.coursePassId };
  };

  const handleAssignmentComplete = async e => {
    e?.preventDefault();
    e?.stopPropagation();

    const formValues = getValues();
    const actionType = hasReservation ? '대기예약' : '배정';

    // 회원 모드일 때 유효성 검사
    let memberData = {};
    if (!guestMode) {
      const validation = validateMemberMode(formValues, actionType);
      if (!validation.isValid) return;
      memberData = {
        memberId: formValues.memberSearch,
        memberCoursePassId: formValues.memberCoursePassId,
      };
    }

    // API 요청 데이터 구성
    // 시작시간은 이미 5초가 더해진 상태이므로 그대로 사용 (초 정보 포함)
    const requestData = {
      startTime: formatDateTime(formValues.startTime, true), // 시작시간임을 표시
      endTime: formatDateTime(formValues.endTime),
      usageMinute: usageMinute,
      bufferMinute: bufferMinute,
      spotMemo: formValues.spotMemo,
      ...memberData,
      ...(hasReservation && { status: 'WAITING' }),
    };

    reservationMutation.mutate(requestData);
  };

  const handleMoveSpot = () => {
    handleClose();
    if (onMoveSpot) {
      onMoveSpot(spotData);
    }
  };

  // 종료 버튼 클릭 시 확인 모달 열기
  const handleEndClick = () => {
    setConfirmEndModalOpen(true);
  };

  // Sell 버튼 클릭 시 선택된 내역 데이터로 필드 채우기
  const handleSellClick = useCallback(
    async row => {
      const status = row.status || row.reservationStatus;
      // 현재 이용중인 경우 모달을 열지 않고 토스트 메시지 표시
      if (status === 'RUNNING') {
        toast.warning('현재 이용중인 스팟입니다.');
        return;
      }
      setSelectedReservationData(row);
      setReservationManageModalOpen(true);
    },
    [toast],
  );

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={modalStyle}>
          {/* 헤더 */}
          <Box
            sx={{
              height: '56px',
              backgroundColor: '#262626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '32px',
            }}>
            <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>
              S{spotNumber} {t('Spot detail')}
            </Typography>
            <CloseIcon sx={{ color: '#fff', width: '32px', height: '32px', cursor: 'pointer' }} onClick={handleClose} />
          </Box>

          {/* 컨텐츠 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
            }}>
            {/* 3개 컬럼 레이아웃 */}
            <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'space-between' }}>
              {/* 왼쪽 컬럼: 스팟 정보 */}
              <Box
                sx={{
                  width: '440px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  justifyContent: 'space-between',
                }}>
                {/* 스팟 회원 & 스팟 정보 */}
                <GolfSpotUserInfo
                  spotData={spotData}
                  isGuestMode={isGuestMode}
                  onWaitingListClick={onWaitingListClick}
                />

                {/* 회원 메모 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                    {t('Member memo')}
                  </Typography>
                  <TextField
                    fullWidth
                    disabled={isGuestMode}
                    placeholder={hasReservation ? '회원 메모를 입력해주세요.' : t('No member is currently using')}
                    defaultValue={memberMemo}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '48px',
                        backgroundColor: isGuestMode ? '#F5F5F5' : 'transparent',
                        '&.Mui-disabled': {
                          backgroundColor: '#F5F5F5',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* 중간 컬럼: 스팟 메모 & 버튼 */}
              <Box
                sx={{
                  width: '440px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  justifyContent: 'space-between',
                }}>
                {/* 스팟 메모 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Spot memo')}</Typography>
                  <Controller
                    name="spotMemo"
                    control={control}
                    defaultValue={spotMemo}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || spotMemo}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="스팟 메모를 입력해주세요"
                      />
                    )}
                  />
                </Box>

                {/* 버튼들 */}
                <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'space-between' }}>
                  <Button
                    disabled={isGuestMode}
                    sx={{
                      width: '120px',
                      height: '48px',
                      backgroundColor: isGuestMode ? '#F5F5F5' : '#C7E8EA',
                      color: isGuestMode ? '#999' : '#000',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      '&:hover': { backgroundColor: isGuestMode ? '#F5F5F5' : '#C7E8EA' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                        color: '#999 !important',
                        WebkitTextFillColor: '#999 !important',
                      },
                    }}
                    onClick={() => {
                      handleMoveSpot();
                    }}>
                    {t('Move spot')}
                  </Button>
                  <Button
                    disabled={isGuestMode}
                    onClick={() => onExtendTimeClick && onExtendTimeClick()}
                    sx={{
                      width: '120px',
                      height: '48px',
                      backgroundColor: isGuestMode ? '#F5F5F5' : '#C7E8EA',
                      color: isGuestMode ? '#999' : '#000',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      '&:hover': { backgroundColor: isGuestMode ? '#F5F5F5' : '#C7E8EA' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                        color: '#999 !important',
                        WebkitTextFillColor: '#999 !important',
                      },
                    }}>
                    {t('Extend time')}
                  </Button>
                  <Button
                    disabled={isGuestMode}
                    onClick={() => handleEndClick()}
                    sx={{
                      width: '120px',
                      height: '48px',
                      backgroundColor: isGuestMode ? '#F5F5F5' : '#EE735A',
                      color: isGuestMode ? '#999' : '#fff',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      '&:hover': { backgroundColor: isGuestMode ? '#F5F5F5' : '#EE735A' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                        color: '#999 !important',
                        WebkitTextFillColor: '#999 !important',
                      },
                    }}>
                    {t('End')}
                  </Button>
                </Box>

                {/* 게스트 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Guest')}</Typography>
                  <Button
                    fullWidth
                    onClick={() => {
                      setIsGuestMode(!isGuestMode);
                      if (!isGuestMode) {
                        // 게스트 모드 활성화 시 회원 검색 및 이용권 초기화
                        setValue('memberSearch', null);
                        setValue('ticket', '');
                        setMemberSearchInput('');
                        setDebouncedSearchInput('');
                        setSelectedMemberId(null);
                        setGuestMode(true);
                      } else {
                        setGuestMode(false);
                        // 게스트 모드 해제 시 회원 선택 및 이용권 초기화
                        setSelectedMemberId(null);
                        setValue('memberSearch', null);
                        setValue('ticket', '');
                        setValue('expirationDate', '');
                      }
                    }}
                    sx={{
                      height: '48px',
                      border: isGuestMode ? '1px solid #0F7F85' : '1px solid #D9D9D9',
                      color: isGuestMode ? '#0F7F85' : '#000',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: '8px',
                      backgroundColor: isGuestMode ? 'none' : 'transparent',
                      '&:hover': { backgroundColor: '#F2F2F2' },
                    }}>
                    {t('Guest entry')}
                  </Button>
                </Box>
              </Box>

              {/* 오른쪽 컬럼: 회원 검색 & 이용권 */}
              <Box
                sx={{
                  width: '440px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  justifyContent: 'space-between',
                }}>
                {/* 회원 검색 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                    {t('Member search')}
                  </Typography>
                  <Controller
                    name="memberSearch"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        disabled={isGuestMode}
                        sx={{
                          width: '100%',
                          '& .MuiAutocomplete-popupIndicator': {
                            transform: 'none',
                          },
                          '& .MuiAutocomplete-popupIndicatorOpen': {
                            transform: 'none',
                          },
                        }}
                        onChange={(e, v) => {
                          if (v) {
                            field.onChange(v.id);
                            setValue('memberSearch', v.id);
                            setSelectedMemberId(v.id); // React Query가 이용권 목록을 가져오도록
                            setFieldErrors(prev => ({ ...prev, memberSearch: false }));
                          } else {
                            field.onChange(null);
                            setValue('memberSearch', null);
                            setSelectedMemberId(null); // 이용권 목록 쿼리 비활성화
                            setValue('ticket', '');
                            setValue('expirationDate', '');
                            setFieldErrors(prev => ({ ...prev, memberSearch: false }));
                          }
                        }}
                        onInputChange={(e, newInputValue, reason) => {
                          if (reason === 'input') {
                            setMemberSearchInput(newInputValue); // debounce된 검색어 업데이트
                            if (
                              newInputValue !== (searchResults.find(option => option.id === field.value)?.name || '')
                            ) {
                              field.onChange(null);
                              setValue('memberSearch', null);
                              setSelectedMemberId(null); // 검색어가 변경되면 선택 해제
                            }
                          }
                        }}
                        loading={isSearchingMembers}
                        value={searchResults.find(option => option.id === field.value) || null}
                        disablePortal
                        options={searchResults}
                        getOptionLabel={option => option?.name || ''}
                        noOptionsText="검색결과가 없습니다"
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.name}
                          </li>
                        )}
                        blurOnSelect={false}
                        clearOnBlur={false}
                        popupIcon={<SearchIcon sx={{ color: '#9a9a9a' }} />}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={fieldErrors.memberSearch && !isGuestMode}
                            placeholder={t('Search by name and contact')}
                            name="memberSearch"
                            label=""
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '48px',
                                padding: 0,
                                borderRadius: '8px',
                                backgroundColor: isGuestMode ? '#F5F5F5' : 'transparent',
                                '&.Mui-disabled': {
                                  backgroundColor: '#F5F5F5',
                                },
                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#d32f2f',
                                  borderWidth: '1px',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '0 8px',
                                fontSize: '14px',
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </Box>

                {/* 이용권 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                    {t('Usage ticket')}
                  </Typography>
                  <FormInputDropdown
                    control={control}
                    name="ticket"
                    sx={{
                      width: '100%',
                      height: '48px',

                      '& .MuiFormControl-root, & .MuiInputBase-root': {
                        backgroundColor: isGuestMode ? '#F5F5F5' : 'transparent',
                        borderRadius: '8px',
                      },

                      '& .Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                        WebkitTextFillColor: '#999 !important',
                      },
                      ...(fieldErrors.ticket && !isGuestMode
                        ? {
                            '& .MuiInputBase-input': {
                              border: '1px solid #d32f2f !important',
                            },
                          }
                        : {}),
                    }}
                    disabled={isGuestMode}
                    placeholder={t('Please select a usage ticket to reserve')}
                    options={ticketOptions}
                    disabledItem={<MenuItem disabled>{t('Please select a usage ticket to reserve')}</MenuItem>}
                    onChange={e => {
                      const selectedTicketId = e.target.value;
                      setFieldErrors(prev => ({ ...prev, ticket: false }));
                      // 선택된 이용권 찾기
                      const selectedTicket = ticketOptions.find(option => option.value === selectedTicketId);
                      if (selectedTicket && selectedTicket.expireDate) {
                        // expireDate를 만료일 필드에 설정
                        setValue('expirationDate', selectedTicket.expireDate);
                        // memberCoursePassId 설정 (shouldValidate와 shouldDirty 옵션 추가)
                        if (selectedTicket.memberCoursePassId) {
                          setValue('memberCoursePassId', selectedTicket.memberCoursePassId, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      } else {
                        // 이용권 선택 해제 시 만료일 필드 초기화
                        setValue('expirationDate', '');
                        setValue('memberCoursePassId', '');
                      }
                    }}
                  />
                </Box>

                {/* 이용시간, 여유시간, 예약시간 */}
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                  {/* 이용시간 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      {t('Usage time')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        type="number"
                        value={usageMinute}
                        onChange={e => {
                          let value = e.target.value;
                          if (value === '') {
                            setUsageMinute('');
                            return;
                          }
                          value = value.replace(/\D/g, '');
                          if (value.startsWith('-')) return;
                          if (value.length > 3) return;
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setUsageMinute(numValue);
                            // 이용시간 변경 시 종료시간 재계산
                            if (!hasReservation && !isRunning && !isStartTimeFocused && !isEndTimeFocused) {
                              const startTime = getValues('startTime');
                              if (startTime) {
                                const calculatedEndTime = calculateEndTime(startTime, numValue);
                                setValue('endTime', calculatedEndTime);
                              }
                            }
                          }
                        }}
                        sx={{
                          width: '96px',
                          '& .MuiInputBase-root': {
                            height: '48px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('minutes')}</Typography>
                    </Box>
                  </Box>

                  {/* 여유시간 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      {t('Buffer time')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        type="number"
                        value={bufferMinute}
                        onChange={e => {
                          let value = e.target.value;
                          if (value === '') {
                            setBufferMinute('');
                            return;
                          }
                          value = value.replace(/\D/g, '');
                          if (value.startsWith('-')) return;
                          if (value.length > 3) return;
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setBufferMinute(numValue);
                            bufferTimeRef.current = numValue;

                            // 여유시간 변경 시 시작시간 업데이트
                            // 시작시간 필드에 포커스가 없을 때만
                            if (!isStartTimeFocused) {
                              let baseTime;
                              let endDate;

                              // 기준 시간 결정: 예약이 있으면 (RESERVED 또는 RUNNING) 예약의 종료시간, 아니면 현재 시각
                              if (hasReservation && spotData?.reservation?.endTime) {
                                try {
                                  endDate = new Date(spotData.reservation.endTime);
                                  if (isNaN(endDate.getTime())) {
                                    endDate = null;
                                    baseTime = formatTimeFromISO(spotData.reservation.endTime);
                                  }
                                } catch (error) {
                                  endDate = null;
                                  baseTime = formatTimeFromISO(spotData.reservation.endTime);
                                }
                              } else {
                                baseTime = calculateStartTime();
                              }

                              if (baseTime || endDate) {
                                // 모든 대기자들의 시간 합산 (여유시간 + 이용시간)
                                let totalWaitingMinutes = 0;
                                if (hasReservation && waitingList.length > 0) {
                                  totalWaitingMinutes = waitingList.reduce((total, waiting) => {
                                    const waitingBufferMinute = waiting.bufferMinute || bufferTimeRef.current;
                                    const waitingUsageMinute = waiting.usageMinute || usageMinute;
                                    return total + waitingBufferMinute + waitingUsageMinute;
                                  }, 0);
                                }

                                // 기준 시간 + 모든 대기자들의 시간 합산 + 5초 = 새로운 시작시간
                                let newStartTime;
                                if (endDate && !isNaN(endDate.getTime())) {
                                  // ISO 문자열에서 초까지 포함해서 계산
                                  const startDate = new Date(endDate.getTime() + totalWaitingMinutes * 60000 + 5000); // 5초 추가
                                  const startHours = String(startDate.getHours()).padStart(2, '0');
                                  const startMinutes = String(startDate.getMinutes()).padStart(2, '0');
                                  const startSeconds = String(startDate.getSeconds()).padStart(2, '0');
                                  newStartTime = `${startHours}:${startMinutes}`;
                                  // 초 정보를 ref에 저장 (전송 시 사용)
                                  startTimeSecondsRef.current = startSeconds;
                                } else {
                                  // 기존 방식 (초 정보 없을 때)
                                  newStartTime = addMinutesToTimeString(baseTime, totalWaitingMinutes);
                                  newStartTime = addSecondsToTimeString(newStartTime, 5);
                                  // addSecondsToTimeString으로 계산된 초 정보 저장
                                  const tempDate = new Date();
                                  const [hours, minutes] = newStartTime.split(':').map(Number);
                                  tempDate.setHours(hours, minutes, 0, 0);
                                  const newDate = new Date(tempDate.getTime() + 5000);
                                  startTimeSecondsRef.current = String(newDate.getSeconds()).padStart(2, '0');
                                }

                                setValue('startTime', newStartTime);
                                // 종료시간 = 시작시간 + 이용시간 + 여유시간
                                const calculatedEndTime = calculateEndTime(newStartTime, usageMinute, numValue);
                                setValue('endTime', calculatedEndTime);
                                // 수동 편집 플래그 설정하여 다른 useEffect가 덮어쓰지 않도록
                                setIsReservationTimeManuallyEdited(false);
                              }
                            }
                          }
                        }}
                        sx={{
                          width: '96px',
                          '& .MuiInputBase-root': {
                            height: '48px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('minutes')}</Typography>
                    </Box>
                  </Box>

                  {/* 예약시간 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, height: '80px' }}>
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* 시작시간 */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          flex: 1,
                        }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>시작시간</Typography>
                        <Controller
                          name="startTime"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="19:00"
                              onFocus={() => {
                                setIsStartTimeFocused(true);
                                setIsReservationTimeManuallyEdited(true);
                              }}
                              onBlur={() => {
                                setIsStartTimeFocused(false);
                              }}
                              onChange={e => {
                                const formattedValue = formatTimeInput(e.target.value);
                                field.onChange(formattedValue);
                                setIsReservationTimeManuallyEdited(true);
                                // 수동으로 변경한 경우 초 정보 초기화 (현재 시간의 초 사용)
                                startTimeSecondsRef.current = null;
                                if (formattedValue.includes(':') && formattedValue.length === 5) {
                                  const newEndTime = calculateEndTime(formattedValue);
                                  if (newEndTime && !isEndTimeFocused) {
                                    setValue('endTime', newEndTime);
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: '48px',
                                },
                              }}
                            />
                          )}
                        />
                      </Box>
                      {/* 종료시간 */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>종료시간</Typography>
                        <Controller
                          name="endTime"
                          control={control}
                          defaultValue={
                            hasReservation
                              ? reservationTime?.split(' ~ ')[1] || ''
                              : calculateEndTime(calculateStartTime())
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              placeholder="20:43"
                              onFocus={() => {
                                setIsEndTimeFocused(true);
                                setIsReservationTimeManuallyEdited(true);
                              }}
                              onBlur={() => {
                                setIsEndTimeFocused(false);
                              }}
                              onChange={e => {
                                const formattedValue = formatTimeInput(e.target.value);
                                field.onChange(formattedValue);
                                setIsReservationTimeManuallyEdited(true);
                              }}
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: '48px',
                                },
                              }}
                              disabled
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* 이용권 잔여시간 & 만료일 */}
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  {/* 이용권 잔여시간 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      {t('Remaining usage ticket time')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        disabled={isGuestMode}
                        sx={{
                          width: '80px',
                          '& .MuiInputBase-root': {
                            height: '48px',
                          },
                          '& .MuiFormControl-root, & .MuiInputBase-root': {
                            backgroundColor: isGuestMode ? '#F5F5F5' : 'transparent',
                            borderRadius: '8px',
                          },

                          '& .Mui-disabled': {
                            backgroundColor: '#F5F5F5 !important',
                            WebkitTextFillColor: '#999 !important',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('hours remaining')}</Typography>
                    </Box>
                  </Box>

                  {/* 이용권 만료일 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      {t('Usage ticket expiration date')}
                    </Typography>
                    <Controller
                      name="expirationDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          disabled={isGuestMode}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: '48px',
                            },
                            '& .MuiFormControl-root, & .MuiInputBase-root': {
                              backgroundColor: isGuestMode ? '#F5F5F5' : 'transparent',
                              borderRadius: '8px',
                            },

                            '& .Mui-disabled': {
                              backgroundColor: '#F5F5F5 !important',
                              WebkitTextFillColor: '#999 !important',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* 스팟 이용 내역 */}
            <GolfSpotHistory spotData={spotData} onSellClick={handleSellClick} />

            {/* 하단 버튼 */}
            <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
              <Button
                sx={{
                  width: '120px',
                  height: '48px',
                  backgroundColor: '#D9D9D9',
                  color: '#000',
                  fontSize: '20px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#D9D9D9' },
                }}
                onClick={handleClose}>
                {t('Cancel')}
              </Button>
              <Button
                sx={{
                  width: '240px',
                  height: '48px',
                  backgroundColor: '#0F7F85',
                  color: '#fff',
                  fontSize: '20px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#0F7F85' },
                }}
                onClick={handleAssignmentComplete}>
                {hasReservation ? t('Waiting reservation') : t('Assignment complete')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* 종료 확인 모달 */}
      <GolfReservationDeleteModal
        open={confirmEndModalOpen}
        onClose={() => setConfirmEndModalOpen(false)}
        reservationId={spotData?.reservation?.reservationId}
        onSuccess={handleClose}
        onLoadingStart={onLoadingStart}
        onLoadingEnd={onLoadingEnd}
      />

      {/* 예약 변경 및 취소 모달 */}
      <GolfReservationManageModal
        open={reservationManageModalOpen}
        handleClose={() => {
          setReservationManageModalOpen(false);
          setSelectedReservationData(null);
        }}
        reservationData={selectedReservationData}
      />
    </>
  );
};

export default GolfSpotDetailModal;
